// The <title> / meta-description composition for every public indexable
// route type, extracted so the page component AND the server's crawler meta
// injection (server/seo/injectHeadTags.js) produce the exact same strings.
// Any drift here shows up as a hydration flicker and a crawler-vs-user
// difference, so the page components import these too — see
// PlacesListPage / GuidesListPage / AboutPage / LandingPage.
import { pickTitle } from "./seoTitle.mjs";

// URL city segments are lowercase (see paths.mjs); real city names in this
// app are single capitalized words. Normalize for display / metadata so a
// directly-visited /places/southampton doesn't read "southampton".
export const cityLabel = (city) => {
  const s = (city || "").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";
};

// /places/:city — the city's "Top Picks" list page.
export const placesListSeo = (city) => {
  const c = cityLabel(city);
  return {
    title: c
      ? `10 Independent Places in ${c} Worth Knowing | Small Potatoes`
      : "Places | Small Potatoes",
    description: c
      ? `Discover 10 independent places in ${c} worth knowing, including cafés, restaurants, cultural venues and local favourites personally selected by Small Potatoes.`
      : "Independent places worth knowing, personally selected by Small Potatoes.",
    h1: c ? `10 Places in ${c} Worth Knowing` : "Places Worth Knowing",
  };
};

// /guides and /guides/:city — the guides index (all cities, or one city).
export const guidesListSeo = (city) => {
  const c = cityLabel(city);
  return c
    ? {
        title: `${c} Guides | Small Potatoes`,
        description: `Curated guides and itineraries for exploring ${c} — Small Potatoes' favourite places, brought together.`,
      }
    : {
        title: "Guides | Small Potatoes",
        description:
          "Curated guides and itineraries for exploring the city — Small Potatoes' favourite places, brought together.",
      };
};

// /about
export const aboutSeo = () => ({
  title: "About Small Potatoes | Places Worth Knowing",
  description:
    "Small Potatoes hand-picks independent places, hidden gems and things worth knowing about in the cities we explore.",
});

// / (homepage)
export const homeSeo = () => ({
  title: "Small Potatoes | Places Worth Knowing",
  description:
    "Discover independent favourites, cultural spots and interesting places worth knowing in Southampton, Birmingham and London.",
});

// Place detail: "{name} — {category}, {city} | Small Potatoes", degrading
// gracefully to stay within the title budget. Description gets " — {type},
// {city}" appended only when the short description is genuinely thin.
export const placePageSeo = (company) => {
  const typeCity = [company.category, company.city].filter(Boolean).join(", ");
  return {
    title: pickTitle([
      typeCity && `${company.name} — ${typeCity} | Small Potatoes`,
      company.city && `${company.name} — ${company.city} | Small Potatoes`,
      `${company.name} | Small Potatoes`,
      company.name,
    ]),
    description:
      company.shortDescription && company.shortDescription.length < 100 && typeCity
        ? `${company.shortDescription} — ${typeCity}`
        : company.shortDescription || undefined,
  };
};

// Guide detail: "{title} — {city} | Small Potatoes", degrading to fit.
export const guidePageSeo = (post) => ({
  title: pickTitle([
    post.city && `${post.title} — ${post.city} | Small Potatoes`,
    `${post.title} | Small Potatoes`,
    post.title,
  ]),
  description: post.excerpt || undefined,
});
